import { useState } from 'react';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import { useAdminList } from './useAdminList';
import type { AdminAiFeature, AdminAiSettings, AdminAiTraceDetail, AdminAiTraceStatus, AdminAiUsage } from '../../../domain/Admin';

const USAGE_DAYS = 7;

// Uso de la IA: lo que gastó (hoy, por día, por versión del prompt) y cada
// llamada al modelo, que se abre entera para ver qué se mandó y qué respondió.
export function useAdminAi() {
  const { admin } = useRepositories();
  const [usage, setUsage] = useState<AdminAiUsage | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [feature, setFeature] = useState<AdminAiFeature | null>(null);
  const [status, setStatus] = useState<AdminAiTraceStatus | null>(null);
  const [selected, setSelected] = useState<AdminAiTraceDetail | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [settings, setSettings] = useState<AdminAiSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  const list = useAdminList(
    (params) => admin.fetchAiTraces({ ...params, feature, status }).then((r) => ({ items: r.traces, total: r.total })),
    [feature, status]
  );

  const loadUsage = () => {
    setUsageError(null);
    admin
      .fetchAiUsage(USAGE_DAYS)
      .then(setUsage)
      .catch((err) => setUsageError(err instanceof Error ? err.message : 'No se pudo cargar el uso.'));
  };
  const loadSettings = () => {
    admin
      .fetchAiSettings()
      .then((r) => setSettings(r.settings))
      .catch((err) => setSettingsError(err instanceof Error ? err.message : 'No se pudo cargar la configuración.'));
  };
  useIonViewWillEnter(() => {
    loadUsage();
    loadSettings();
  });

  // Cada cambio se guarda en el momento (no hay botón "guardar").
  const saveSettings = async (input: Partial<AdminAiSettings>) => {
    setSavingSettings(true);
    setSettingsError(null);
    try {
      setSettings((await admin.updateAiSettings(input)).settings);
    } catch (err) {
      setSettingsError(err instanceof Error ? err.message : 'No se pudo guardar.');
      loadSettings();
    } finally {
      setSavingSettings(false);
    }
  };

  const open = async (id: string) => {
    setOpening(id);
    try {
      setSelected((await admin.fetchAiTrace(id)).trace);
    } catch (err) {
      setUsageError(err instanceof Error ? err.message : 'No se pudo abrir la llamada.');
    } finally {
      setOpening(null);
    }
  };

  return {
    ...list,
    usage,
    usageError,
    feature,
    setFeature,
    status,
    setStatus,
    selected,
    opening,
    open,
    close: () => setSelected(null),
    settings,
    savingSettings,
    settingsError,
    saveSettings,
    refresh: () => {
      loadUsage();
      loadSettings();
      list.reload();
    },
  };
}
